import * as React from "react";
import {
    Control,
    Controller,
    FormState,
    UseFormRegister,
    UseFormWatch,
} from "react-hook-form";
import { DatePicker, Input, RadioInput } from "@jhool-io/fe-components";
import {
    IClaimDetailsContent,
    IClaimInsuranceType,
    SingleClaim,
} from "../../../types/claims.types";

interface PersonalInfoFormProps {
    register: UseFormRegister<IClaimDetailsContent>;
    formState: FormState<IClaimDetailsContent>;
    control: Control<IClaimDetailsContent, unknown>;
    watch: UseFormWatch<IClaimDetailsContent>;
    claimDetails: SingleClaim;
}

export default function PersonalInfoForm({
    claimDetails,
    register,
    control,
    watch,
    formState: { errors },
}: PersonalInfoFormProps) {
    const watchOtherInsurancePlanProgram = watch("otherInsurancePlanProgram");
    const watchOtherSubscriberFirstName = watch("otherSubscriberFirstName");
    const [hasAnotherPlan, setHasAnotherPlan] = React.useState(
        () =>
            Boolean(watchOtherInsurancePlanProgram) &&
            Boolean(watchOtherSubscriberFirstName)
    );

    const claimContent = claimDetails.claim_content;

    return (
        <div className="py-[24px] [border-bottom:1px_solid_var(--color-stroke)]">
            <h4 className="text-sm font-semibold mb-[--spacing-header] uppercase md:sticky top-20 z-[4] text-textLink">
                Personal & Insured Info
            </h4>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5
                        className="text-caption font-semibold
                    leading-tight mb-2
                    "
                    >
                        Insurance Type
                    </h5>
                </div>

                <div className="flex-[3]">
                    <div className="flex flex-wrap lg:grid grid-cols-3 gap-[--spacing-form-items] max-w-[80%]">
                        <div className="relative">
                            <RadioInput
                                {...register("insuranceType")}
                                label="Medicare"
                                defaultChecked={
                                    claimContent?.insuranceType ===
                                    IClaimInsuranceType.MEDICARE
                                }
                                value={IClaimInsuranceType.MEDICARE}
                                wrapperClass="min-w-[85px]"
                            />
                            <p className="text-textLink text-caption font-semibold ml-2 absolute top-[50%] -translate-y-[50%] left-[90px] hidden md:block">
                                (Medicare#)
                            </p>
                        </div>
                        <div className="relative">
                            <RadioInput
                                {...register("insuranceType")}
                                label="Medicaid"
                                defaultChecked={
                                    claimContent?.insuranceType ===
                                    IClaimInsuranceType.MEDICAID
                                }
                                value={IClaimInsuranceType.MEDICAID}
                                wrapperClass="min-w-[85px]"
                            />
                            <span className="text-textLink text-caption font-semibold ml-2 absolute top-[50%] -translate-y-[50%] left-[90px] hidden md:block">
                                (Medicaid#)
                            </span>
                        </div>
                        <div className="relative">
                            <RadioInput
                                {...register("insuranceType")}
                                label="Tricare"
                                defaultChecked={
                                    claimContent?.insuranceType ===
                                    IClaimInsuranceType.TRICARE
                                }
                                value={IClaimInsuranceType.TRICARE}
                                wrapperClass="min-w-[85px]"
                            />
                            <span className="text-textLink text-caption font-semibold ml-2 absolute top-[50%] -translate-y-[50%] left-[90px] hidden md:block">
                                (ID/DOD#)
                            </span>
                        </div>

                        <div className="relative">
                            <RadioInput
                                {...register("insuranceType")}
                                label="Champva"
                                defaultChecked={
                                    claimContent?.insuranceType ===
                                    IClaimInsuranceType.CHAMPVA
                                }
                                value={IClaimInsuranceType.CHAMPVA}
                                wrapperClass="min-w-[85px]"
                            />
                            <span className="text-textLink text-caption font-semibold ml-2 absolute top-[50%] -translate-y-[50%] left-[90px] hidden md:block">
                                (Member ID#)
                            </span>
                        </div>

                        <div className="relative">
                            <RadioInput
                                {...register("insuranceType")}
                                label="Group Health Plan"
                                defaultChecked={
                                    claimContent?.insuranceType ===
                                    IClaimInsuranceType.GROUP_HEALTH_PLAN
                                }
                                value={IClaimInsuranceType.GROUP_HEALTH_PLAN}
                                wrapperClass="min-w-[85px]"
                            />
                            <span className="text-textLink text-caption font-semibold ml-2 absolute top-[50%] -translate-y-[50%] left-[140px] hidden md:block">
                                (ID#)
                            </span>
                        </div>

                        <div className="relative">
                            <RadioInput
                                {...register("insuranceType")}
                                label="FECA BLK LNG"
                                defaultChecked={
                                    claimContent?.insuranceType ===
                                    IClaimInsuranceType.FECA_BLK_LNG
                                }
                                value={IClaimInsuranceType.FECA_BLK_LNG}
                                wrapperClass="min-w-[85px]"
                            />
                            <span className="text-textLink text-caption font-semibold ml-2 absolute top-[50%] -translate-y-[50%] left-[120px] hidden md:block">
                                (ID#)
                            </span>
                        </div>

                        <div className="relative">
                            <RadioInput
                                {...register("insuranceType")}
                                label="Other"
                                defaultChecked={
                                    claimContent?.insuranceType ===
                                    IClaimInsuranceType.OTHER
                                }
                                value={IClaimInsuranceType.OTHER}
                                wrapperClass="min-w-[85px]"
                            />
                            <span className="text-textLink text-caption font-semibold ml-2  absolute top-[50%] -translate-y-[50%] left-[70px] hidden md:block">
                                (ID#)
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-caption font-semibold leading-tight mb-2">
                        Insured&apos;s ID Number
                    </h5>
                </div>

                <div className="flex-[3] flex flex-wrap gap-[--spacing-form-items]">
                    <Input
                        placeholder="AB1234567"
                        className="md:max-w-[40%]"
                        {...register("subscriberId")}
                        hasError={!!errors.subscriberId}
                        errorText={errors.subscriberId?.message}
                    />
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-caption font-semibold leading-tight mb-2">
                        Insured&apos;s Name
                    </h5>
                    <p className="text-textSec mb-[0.6rem] text-caption font-semibold">
                        (Last Name, First name, Middle Initial)
                    </p>
                </div>

                <div className="flex-[3]">
                    <div className="flex flex-col md:flex-row gap-[--spacing-form-items] md:max-w-[80%]">
                        <Input
                            placeholder="Last Name"
                            {...register("subscriberLastName")}
                            hasError={!!errors.subscriberLastName}
                            errorText={errors.subscriberLastName?.message}
                        />

                        <Input
                            placeholder="First Name"
                            {...register("subscriberFirstName")}
                            hasError={!!errors.subscriberFirstName}
                            errorText={errors.subscriberFirstName?.message}
                        />

                        <Input
                            placeholder="Middle Initial"
                            {...register("subscriberMiddleName")}
                            hasError={!!errors.subscriberMiddleName}
                            errorText={errors.subscriberMiddleName?.message}
                        />
                    </div>
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-caption font-semibold leading-tight mb-2">
                        Insured&apos;s Address
                    </h5>
                    <p className="text-textSec mb-[0.6rem] text-caption font-semibold">
                        (No., Street)
                    </p>
                </div>

                <div className="flex-[3]">
                    <div className="flex flex-wrap gap-[--spacing-form-items] md:max-w-[80%]">
                        <Input
                            placeholder="Brooklyn, Upper East Side, Financial District"
                            className="w-full"
                            {...register("subscriberAddress")}
                            hasError={!!errors.subscriberAddress}
                            errorText={errors.subscriberAddress?.message}
                        />
                        <div className="flex flex-col md:flex-row items-center gap-[--spacing-form-items] w-full">
                            <Input
                                placeholder="Brooklyn"
                                {...register("subscriberCity")}
                                hasError={!!errors.subscriberCity}
                                errorText={errors.subscriberCity?.message}
                            />
                            <Input
                                placeholder="NY"
                                {...register("subscriberState")}
                                hasError={!!errors.subscriberState}
                                errorText={errors.subscriberState?.message}
                            />
                            <Input
                                placeholder="(01) 159 969 739"
                                {...register("subscriberPhone")}
                                hasError={!!errors.subscriberPhone}
                                errorText={errors.subscriberPhone?.message}
                            />
                            <Input
                                placeholder="11208"
                                {...register("subscriberZip")}
                                hasError={!!errors.subscriberZip}
                                errorText={errors.subscriberZip?.message}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-caption font-semibold leading-tight mb-2">
                        Insured&apos;s Policy Group or FECA Number
                    </h5>
                </div>

                <div className="flex-[3] flex flex-wrap gap-[--spacing-form-items]">
                    <Input
                        placeholder="1234567"
                        className="md:max-w-[40%]"
                        {...register("subscriberPolicyGroup")}
                        hasError={!!errors.subscriberPolicyGroup}
                        errorText={errors.subscriberPolicyGroup?.message}
                    />
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-caption font-semibold leading-tight mb-2">
                        Insurance Plan Name or Program Name
                    </h5>
                </div>

                <div className="flex-[3] flex flex-wrap gap-[--spacing-form-items]">
                    <Input
                        placeholder="Other"
                        className="md:max-w-[40%]"
                        {...register("insuranceGroupName")}
                        hasError={!!errors.insuranceGroupName}
                        errorText={errors.insuranceGroupName?.message}
                    />
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-caption font-semibold leading-tight mb-2">
                        Insured&apos;s Date of Birth
                    </h5>
                </div>

                <div className="flex-[3]">
                    <div className="flex flex-wrap gap-[--spacing-form-items] lg:max-w-[40%]">
                        <Controller
                            control={control}
                            name="subscriberBirthdate"
                            render={({ field }) => (
                                <DatePicker
                                    selected={
                                        field.value
                                            ? new Date(field.value)
                                            : null
                                    }
                                    value={field.value}
                                    onChange={(date) => {
                                        field.onChange(date);
                                    }}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-caption font-semibold leading-tight mb-2">
                        Insured&apos;s Sex
                    </h5>
                </div>

                <div className="flex-[3] flex flex-wrap gap-[--spacing-form-items]">
                    <RadioInput
                        {...register("subscriberSex")}
                        label="M"
                        defaultChecked={claimContent?.subscriberSex === "M"}
                        value="M"
                        wrapperClass="min-w-[125px]"
                    />
                    <RadioInput
                        {...register("subscriberSex")}
                        label="F"
                        defaultChecked={claimContent?.subscriberSex === "F"}
                        value="F"
                        wrapperClass="min-w-[125px]"
                    />
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-caption font-semibold leading-tight mb-2">
                        Patient&apos;s Name
                    </h5>
                    <p className="text-textSec mb-[0.6rem] text-caption font-semibold">
                        (Last Name, First name, Middle Initial)
                    </p>
                </div>

                <div className="flex-[3] ">
                    <div className="flex flex-col md:flex-row gap-[--spacing-form-items] md:max-w-[80%]">
                        <Input
                            placeholder="Last Name"
                            {...register("patientLastName")}
                            hasError={!!errors.patientLastName}
                            errorText={errors.patientLastName?.message}
                        />

                        <Input
                            placeholder="First Name"
                            {...register("patientFirstName")}
                            hasError={!!errors.patientFirstName}
                            errorText={errors.patientFirstName?.message}
                        />

                        <Input
                            placeholder="Middle Initial"
                            {...register("patientMiddleName")}
                            hasError={!!errors.patientMiddleName}
                            errorText={errors.patientMiddleName?.message}
                        />
                    </div>
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-caption font-semibold leading-tight mb-2">
                        Patient&apos;s Address
                    </h5>
                    <p className="text-textSec mb-[0.6rem] text-caption font-semibold">
                        (No., Street)
                    </p>
                </div>

                <div className="flex-[3]">
                    <div className="flex flex-wrap gap-[--spacing-form-items] md:max-w-[80%]">
                        <Input
                            placeholder="Brooklyn, Upper East Side, Financial District"
                            className="w-full"
                            {...register("patientAddress")}
                            hasError={!!errors.patientAddress}
                            errorText={errors.patientAddress?.message}
                        />
                        <div className="flex flex-col md:flex-row items-center gap-[--spacing-form-items] w-full">
                            <Input
                                placeholder="Brooklyn"
                                {...register("patientCity")}
                                hasError={!!errors.patientCity}
                                errorText={errors.patientCity?.message}
                            />
                            <Input
                                placeholder="NY"
                                {...register("patientState")}
                                hasError={!!errors.patientState}
                                errorText={errors.patientState?.message}
                            />
                            <Input
                                placeholder="(01) 159 969 739"
                                {...register("patientPhone")}
                                hasError={!!errors.patientPhone}
                                errorText={errors.patientPhone?.message}
                            />
                            <Input
                                placeholder="11208"
                                {...register("patientZip")}
                                hasError={!!errors.patientZip}
                                errorText={errors.patientZip?.message}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-caption font-semibold leading-tight mb-2">
                        Patient&apos;s Date of Birth
                    </h5>
                </div>

                <div className="flex-[3]">
                    <div className="flex flex-wrap gap-[--spacing-form-items] md:max-w-[40%]">
                        <Controller
                            control={control}
                            name="patientBirthdate"
                            render={({ field }) => (
                                <DatePicker
                                    selected={
                                        field.value
                                            ? new Date(field.value)
                                            : null
                                    }
                                    value={field.value}
                                    onChange={(date) => {
                                        field.onChange(date);
                                    }}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className=" text-caption font-semibold leading-tight mb-2">
                        Patient&apos;s Sex
                    </h5>
                </div>

                <div className="flex-[3] flex flex-wrap gap-[--spacing-form-items]">
                    <RadioInput
                        {...register("patientSex")}
                        label="M"
                        defaultChecked={claimContent?.patientSex === "M"}
                        value="M"
                        wrapperClass="min-w-[120px]"
                    />
                    <RadioInput
                        {...register("patientSex")}
                        label="F"
                        defaultChecked={claimContent?.patientSex === "F"}
                        value="F"
                        wrapperClass="min-w-[120px]"
                    />
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-caption font-semibold leading-tight mb-2">
                        Patient&apos;s Relationship to the Insured
                    </h5>
                </div>

                <div className="flex-[3] flex flex-wrap gap-[--spacing-form-items]">
                    <RadioInput
                        {...register("patientRelationshipToSubscriber")}
                        label="Self"
                        wrapperClass="min-w-[120px]"
                        defaultChecked={
                            claimContent?.patientRelationshipToSubscriber ===
                            "18"
                        }
                        value="18"
                    />
                    <RadioInput
                        {...register("patientRelationshipToSubscriber")}
                        label="Spouse"
                        wrapperClass="min-w-[120px]"
                        defaultChecked={
                            claimContent?.patientRelationshipToSubscriber ===
                            "01"
                        }
                        value="01"
                    />
                    <RadioInput
                        {...register("patientRelationshipToSubscriber")}
                        label="Child"
                        wrapperClass="min-w-[120px]"
                        defaultChecked={
                            claimContent?.patientRelationshipToSubscriber ===
                            "19"
                        }
                        value="19"
                    />

                    <RadioInput
                        {...register("patientRelationshipToSubscriber")}
                        label="Other"
                        wrapperClass="min-w-[120px]"
                        defaultChecked={
                            claimContent?.patientRelationshipToSubscriber ===
                            "G8"
                        }
                        value="G8"
                    />
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-caption font-semibold leading-tight mb-2">
                        Is Patient&apos;s Condition related to
                    </h5>
                </div>

                <div className="flex-[3]">
                    <div className="flex flex-wrap gap-[--spacing-form-items] md:max-w-[80%]">
                        <div>
                            <h6 className="text-textSec text-caption font-medium mb-1">
                                Employment
                            </h6>
                            <div className="flex items-center gap-[--spacing-form-items]">
                                <RadioInput
                                    {...register(
                                        "isConditionRelatedToEmployment"
                                    )}
                                    label="Yes"
                                    wrapperClass="min-w-[80px]"
                                    value="Y"
                                    defaultChecked={
                                        claimContent?.isConditionRelatedToEmployment ===
                                        "Y"
                                    }
                                />
                                <RadioInput
                                    {...register(
                                        "isConditionRelatedToEmployment"
                                    )}
                                    label="No"
                                    wrapperClass="min-w-[80px]"
                                    value="N"
                                    defaultChecked={
                                        claimContent?.isConditionRelatedToEmployment ===
                                        "N"
                                    }
                                />
                            </div>
                        </div>

                        <div>
                            <h6 className="text-textSec text-caption font-medium mb-1">
                                Auto accident
                            </h6>

                            <div className="flex items-center gap-[--spacing-form-items]">
                                <RadioInput
                                    {...register(
                                        "isConditionAutoAccidentRelated"
                                    )}
                                    label="Yes"
                                    wrapperClass="min-w-[80px]"
                                    value="Y"
                                    defaultChecked={
                                        claimContent?.isConditionAutoAccidentRelated ===
                                        "Y"
                                    }
                                />
                                <RadioInput
                                    {...register(
                                        "isConditionAutoAccidentRelated"
                                    )}
                                    label="No"
                                    wrapperClass="min-w-[80px]"
                                    value="N"
                                    defaultChecked={
                                        claimContent?.isConditionAutoAccidentRelated ===
                                        "N"
                                    }
                                />
                            </div>
                        </div>

                        <div>
                            <h6 className="text-textSec text-caption font-medium mb-1">
                                Other accident
                            </h6>

                            <div className="flex items-center gap-[--spacing-form-items]">
                                <RadioInput
                                    {...register(
                                        "isConditionOtherAccidentRelated"
                                    )}
                                    label="Yes"
                                    wrapperClass="min-w-[80px]"
                                    value="Y"
                                    defaultChecked={
                                        claimContent?.isConditionOtherAccidentRelated ===
                                        "Y"
                                    }
                                />
                                <RadioInput
                                    {...register(
                                        "isConditionOtherAccidentRelated"
                                    )}
                                    label="No"
                                    wrapperClass="min-w-[80px]"
                                    value="N"
                                    defaultChecked={
                                        claimContent?.isConditionOtherAccidentRelated ===
                                        "N"
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    {watch("isConditionAutoAccidentRelated") === "Y" ? (
                        <div className="w-full my-3">
                            <h6 className="text-textSec text-caption font-medium mb-1">
                                Place(State)
                            </h6>
                            <Input
                                placeholder="Brooklyn, Upper East Side, Financial District"
                                className="max-w-[40%]"
                                {...register("autoAccidentState")}
                                hasError={!!errors.autoAccidentState}
                                errorText={errors.autoAccidentState?.message}
                            />
                        </div>
                    ) : null}
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-caption font-semibold leading-tight mb-2">
                        Is there another Health Benefit Plan
                    </h5>
                </div>
                <div className="flex-[3] flex flex-wrap gap-[--spacing-form-items]">
                    <RadioInput
                        label="Yes"
                        wrapperClass="min-w-[120px]"
                        checked={hasAnotherPlan}
                        onChange={() => setHasAnotherPlan(true)}
                    />

                    <RadioInput
                        label="No"
                        wrapperClass="min-w-[120px]"
                        checked={!hasAnotherPlan}
                        onChange={() => setHasAnotherPlan(false)}
                    />
                </div>
            </div>

            {hasAnotherPlan ? (
                <>
                    <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                        <div className="flex-1">
                            <h5 className="text-caption font-semibold leading-tight mb-2">
                                Other Insured&apos; Name
                            </h5>
                            <p className="text-textSec mb-[0.6rem] text-caption font-semibold">
                                (Last Name, First name, Middle Initial)
                            </p>
                        </div>

                        <div className="flex-[3] flex flex-wrap gap-[--spacing-form-items]">
                            <Input
                                placeholder="Woods, Nathaniel, A"
                                className="md:max-w-[40%]"
                                {...register("otherSubscriberFirstName")}
                                hasError={!!errors.otherSubscriberFirstName}
                                errorText={
                                    errors.otherSubscriberFirstName?.message
                                }
                            />
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                        <div className="flex-1">
                            <h5 className="text-caption font-semibold leading-tight mb-2">
                                Other Insured&apos;s Policy Or Group Number
                            </h5>
                        </div>

                        <div className="flex-[3] flex flex-wrap gap-[--spacing-form-items]">
                            <Input
                                placeholder="124110"
                                className="md:max-w-[40%]"
                                {...register("otherSubscriberId")}
                                hasError={!!errors.otherSubscriberId}
                                errorText={errors.otherSubscriberId?.message}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                        <div className="flex-1">
                            <h5 className="text-caption font-semibold leading-tight mb-2">
                                Insurance Plan Name or Program Name
                            </h5>
                        </div>

                        <div className="flex-[3] flex flex-wrap gap-[--spacing-form-items]">
                            <Input
                                placeholder="Program Name"
                                className="md:max-w-[40%]"
                                {...register("otherInsurancePlanProgram")}
                                hasError={!!errors.otherInsurancePlanProgram}
                                errorText={
                                    errors.otherInsurancePlanProgram?.message
                                }
                            />
                        </div>
                    </div>
                </>
            ) : null}

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-caption font-semibold leading-tight mb-2">
                        Patient&apos;s or Authorized Person&apos;s Signature
                    </h5>
                    <p className="text-textSec mb-[0.6rem] text-caption font-semibold">
                        I authorise the release of any medical or other
                        information necessary to process this claim. I also
                        request payment of government benefits either to myself
                        or to the party who accepts assignment below.
                    </p>
                </div>

                <div className="flex-[3] flex flex-wrap gap-[--spacing-form-items]">
                    <div className="min-w-[260px]">
                        <h6 className="text-textSec text-caption font-medium mb-1">
                            Signed
                        </h6>
                        <Input
                            placeholder="Signature"
                            {...register("patientSignature")}
                            hasError={!!errors.patientSignature}
                            errorText={errors.patientSignature?.message}
                        />
                    </div>

                    <div>
                        <h6 className="text-textSec text-caption font-medium mb-1">
                            Date
                        </h6>
                        <div className="flex items-center gap-4">
                            <Controller
                                control={control}
                                name="patientSignatureDate"
                                render={({ field }) => (
                                    <DatePicker
                                        selected={
                                            field.value
                                                ? new Date(field.value)
                                                : null
                                        }
                                        onChange={(date) => {
                                            field.onChange(date ?? null);
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-[--spacing-form-items] mb-[--spacing-header]">
                <div className="flex-1">
                    <h5 className="text-caption font-semibold leading-tight mb-2">
                        Insured&apos;s or Authorized Person&apos;s Signature
                    </h5>
                    <p className="text-textSec mb-[0.6rem] text-caption font-semibold">
                        I authorise payment of medical benefits to the
                        undersigned physician or supplier for services described
                        below.
                    </p>
                </div>

                <div className="flex-[3] flex flex-wrap gap-[--spacing-form-items]">
                    <div className="min-w-[260px]">
                        <h6 className="text-textSec text-caption font-medium mb-1">
                            Signed
                        </h6>
                        <Input
                            placeholder="Signature"
                            {...register("subscriberSignature")}
                            hasError={!!errors.subscriberSignature}
                            errorText={errors.subscriberSignature?.message}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
